import { Box, Tab, Tabs, Typography, makeStyles } from '@material-ui/core'
import React from 'react'

import { HeaderSlotContext } from 'app/pages/Apartment/Procurement/roleContext'

import HqAfterschool from './HqAfterschool'
import { HqCapexBudgets, HqCapexReview, HqCapexSetup } from './HqCapex'
import HqCatalogue from './HqCatalogue'
import HqDailySheet from './HqDailySheet'
import HqDashboard from './HqDashboard'
import HqHome from './HqHome'
import HqMonthlyBoard from './HqMonthlyBoard'
import HqSop from './HqSop'
import HqVendors from './HqVendors'

import BudgetsPage from '../Budgets'
import ProcurementThemeProvider from '../components/ProcurementThemeProvider'
import NotificationsPage from '../Notifications'

// All four categories share one rich all-centres monthly board (the combined
// board was dropped 2026-07-21 for per-category tabs).
const HqProgramMaterialBoard = () => <HqMonthlyBoard category="pm" />
const HqConsumablesBoard = () => <HqMonthlyBoard category="consumables" />
const HqPettyCashBoard = () => <HqMonthlyBoard category="petty_cash" />
const HqRmBoard = () => <HqMonthlyBoard category="rm" />

// HQ/admin view: cross-centre procurement administration via top tabs.
// Grouped nav (user 2026-07-19): the four monthly views collapse under a single
// "Monthly Cadence" top tab, and the reference/admin views (Catalogue, SOP,
// Vendor list) collapse under "Settings". Each group opens a sub-tab row. This
// keeps the top bar to: Overview · Monthly Cadence · Daily Sheet · Settings.
// `tab` always holds the LEAF id, so the overview cards' onNavigate(leafId) and
// any deep links keep working — the active group is derived from the leaf.
const NAV = [
  { id: 'home', label: 'Overview', Comp: HqHome },
  {
    id: 'monthly',
    label: 'Monthly Cadence',
    children: [
      { id: 'dashboard', label: 'Dashboard', Comp: HqDashboard },
      { id: 'budgets', label: 'Budgets', Comp: BudgetsPage },
      {
        id: 'pending-orders',
        label: 'Program Material',
        Comp: HqProgramMaterialBoard
      },
      { id: 'consumables', label: 'Consumables', Comp: HqConsumablesBoard },
      { id: 'petty-cash', label: 'Petty Cash', Comp: HqPettyCashBoard },
      { id: 'rm', label: 'R&M', Comp: HqRmBoard }
    ]
  },
  { id: 'daily-sheet', label: 'Daily Sheet', Comp: HqDailySheet },
  {
    id: 'capex',
    // Named to match the centre view exactly (user 2026-08-22) — the two nav
    // bars read as one system.
    label: 'Monthly Capex',
    children: [
      { id: 'capex-setup', label: 'Month setup', Comp: HqCapexSetup },
      { id: 'capex-budgets', label: 'Budgets', Comp: HqCapexBudgets },
      { id: 'capex-review', label: 'Review', Comp: HqCapexReview }
    ]
  },
  { id: 'afterschool', label: 'Afterschool Games', Comp: HqAfterschool },
  { id: 'notifications', label: 'Notifications', Comp: NotificationsPage },
  {
    id: 'settings',
    label: 'Settings',
    children: [
      { id: 'catalogue', label: 'Catalogue', Comp: HqCatalogue },
      { id: 'sop', label: 'SOP', Comp: HqSop },
      { id: 'vendors', label: 'Vendor list', Comp: HqVendors }
    ]
  }
]

// The group a leaf id belongs to (or the top entry itself when it has no
// children). Defaults to Overview for an unknown id.
const groupOf = (leafId) =>
  NAV.find(
    (n) => n.id === leafId || (n.children || []).some((c) => c.id === leafId)
  ) || NAV[0]

const TAB_KEY = 'procurement.hq.tab'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  title: { fontWeight: 700, marginBottom: theme.spacing(1) },
  tabs: { borderBottom: '1px solid #eee', marginBottom: theme.spacing(1) },
  subtabs: { marginBottom: theme.spacing(1.5) }
}))

const ProcurementHq = () => {
  const classes = useStyles()
  const [headerSlot, setHeaderSlot] = React.useState(null)
  // Always land on the hub (user 2026-07-17): the boxes say what needs
  // attention; every area is one click away.
  const [tab, setTab] = React.useState('home')

  React.useEffect(() => {
    window.localStorage.setItem(TAB_KEY, tab)
  }, [tab])

  const group = groupOf(tab)
  const Active = group.children
    ? (group.children.find((c) => c.id === tab) || group.children[0]).Comp
    : group.Comp
  const activeProps = tab === 'home' ? { onNavigate: setTab } : {}

  // Clicking a top tab: land on its first sub-view (unless we're already inside
  // that group, in which case keep the current sub-view).
  const onTopChange = (topId) => {
    const top = NAV.find((n) => n.id === topId) || NAV[0]
    if (top.children) {
      const inside = top.children.some((c) => c.id === tab)
      setTab(inside ? tab : top.children[0].id)
    } else {
      setTab(top.id)
    }
  }

  return (
    <ProcurementThemeProvider>
      <Box className={classes.root}>
        <Typography variant="h5" className={classes.title}>
          HQ · Procurement & Expenses
        </Typography>
        <Box className={classes.tabs} display="flex" alignItems="center">
          <Tabs
            style={{ flex: 1 }}
            value={group.id}
            onChange={(e, v) => onTopChange(v)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            {NAV.map((t) => (
              <Tab key={t.id} value={t.id} label={t.label} />
            ))}
          </Tabs>
          <Box
            ref={setHeaderSlot}
            display="flex"
            alignItems="center"
            style={{ gap: 8 }}
          />
        </Box>
        {group.children && (
          <Box className={classes.subtabs}>
            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              {group.children.map((c) => (
                <Tab key={c.id} value={c.id} label={c.label} />
              ))}
            </Tabs>
          </Box>
        )}
        <HeaderSlotContext.Provider value={headerSlot}>
          <Active {...activeProps} />
        </HeaderSlotContext.Provider>
      </Box>
    </ProcurementThemeProvider>
  )
}

export default ProcurementHq
